import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import warning from "../../images/warning.svg";

export default function MultiSectionModal({
  isOpen,
  onClose,
  onConfirm,
  title = "Delete",
  description = "Are you sure you want to delete this item?",
  confirmButtonText = "Delete",
  cancelButtonText = "Cancel",
  confirmButtonColor = "bg-red-600 hover:bg-red-800",
  icon = warning,
  selectedRecipientIds = [],
}) {
  const [confirmationStep, setConfirmationStep] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  function closeModal(e) {
    if (e) e.stopPropagation();
    setConfirmationStep(false);
    setConfirmationText("");
    onClose();
  }

  const handleConfirm = async () => {
    if (!confirmationStep) {
      setConfirmationStep(true);
      return;
    }

    if (confirmationText.trim() !== "DELETE") {
      return;
    }

    setIsDeleting(true);
    try {
      await onConfirm();
      closeModal();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              className="fixed inset-0 bg-black/25 backdrop-blur-md"
              onClick={closeModal}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && closeModal(e)}
            />
          </Transition.Child>

          <div
            className="fixed inset-0 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && e.stopPropagation()}
          >
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all relative">
                  <button
                    type="button"
                    className="absolute top-3 right-3 text-gray-400 hover:text-gray-500 focus:outline-none"
                    onClick={closeModal}
                  >
                    <span className="sr-only">Close</span>
                    &#10005;
                  </button>

                  <Dialog.Title
                    as="h3"
                    className="text-lg flex justify-center font-medium leading-6 text-gray-900"
                  >
                    {icon && <img src={icon} alt="warning" />}
                  </Dialog.Title>
                  <div className="mt-2">
                    <p className="text-lg font-medium flex justify-center">
                      {confirmationStep ? "Confirm Deletion" : title}
                    </p>
                    <div className="text-sm text-gray-500">
                      {confirmationStep ? (
                        <div className="space-y-2">
                          <p>
                            To confirm you want to delete{" "}
                            {selectedRecipientIds.length} issued certificate
                            {selectedRecipientIds.length > 1 ? "s" : ""}, please
                            type <span className="font-bold">DELETE</span> in
                            the box below.
                          </p>
                          <input
                            type="text"
                            className="bg-gray-50 border border-gray-300 text-gray-600 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                            placeholder="Type DELETE to confirm"
                            value={confirmationText}
                            onChange={(e) =>
                              setConfirmationText(e.target.value)
                            }
                          />
                        </div>
                      ) : (
                        description
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex justify-between">
                    <button
                      type="button"
                      onClick={closeModal}
                      className="py-2.5 px-5 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 focus:z-10 focus:ring-3 focus:ring-gray-100"
                    >
                      {cancelButtonText}
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirm}
                      disabled={
                        (confirmationStep &&
                          confirmationText.trim() !== "DELETE") ||
                        isDeleting
                      }
                      className={`text-white focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm inline-flex items-center px-5 py-2.5 text-center ${
                        confirmationStep && confirmationText.trim() !== "DELETE"
                          ? "bg-gray-400 cursor-not-allowed"
                          : confirmButtonColor
                      }`}
                    >
                      {isDeleting
                        ? "Deleting..."
                        : confirmationStep
                        ? "Submit"
                        : confirmButtonText}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
